<template>
  <div class="get-app-page">
    <HeadNav selectedLanguage="en" page="download" />

    <main>
      <!-- 文档：H1 + Logo 图 -->
      <section class="hero-shell" aria-labelledby="download-title">
        <div class="hero-shell__glow" aria-hidden="true"></div>
        <div class="section-inner hero-section">
          <h1 id="download-title">Safe Page to Download SuperVPN App</h1>
          <figure class="logo-figure">
            <img
              :src="logo"
              alt="SuperVPN logo"
              width="480"
              height="480"
              loading="eager"
            >
          </figure>
          <div class="hero-actions">
            <button type="button" class="primary-btn" @click="handleDownload">
              <svg class="primary-btn__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M12 3.25a.75.75 0 0 1 .75.75v9.19l2.72-2.72a.75.75 0 1 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-4-4a.75.75 0 1 1 1.06-1.06l2.72 2.72V4a.75.75 0 0 1 .75-.75Zm-6.5 14.5a.75.75 0 0 0 0 1.5h13a.75.75 0 0 0 0-1.5h-13Z"
                />
              </svg>
              <span>Get APK</span>
            </button>
          </div>
        </div>
      </section>

      <!-- 文档：SuperVPN Installation Requirements + 表格 -->
      <section class="section" aria-labelledby="requirements-title">
        <div class="section-inner">
          <h2 id="requirements-title">SuperVPN Installation Requirements</h2>
          <p>Before installing SuperVPN APK, check the following:</p>
          <div class="table-wrap">
            <table aria-label="SuperVPN installation requirements">
              <thead>
                <tr>
                  <th>Requirement</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in requirementRows" :key="row.label">
                  <td>{{ row.label }}</td>
                  <td>{{ row.value }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- 文档：How to Set Up SuperVPN on Android -->
      <section class="section setup-section" aria-labelledby="guide-title">
        <div class="section-inner">
          <h2 id="guide-title">
            <router-link class="section-title-link" to="/how-to-set-up-supervpn/">
              How to Set Up SuperVPN on Android
            </router-link>
          </h2>
          <p>
            SuperVPN is designed to be easy to use. No complicated configuration or advanced network settings are required.
          </p>
          <p>Follow these steps:</p>

          <div class="setup-list">
            <article class="setup-item">
              <h3>1. Open SuperVPN</h3>
              <p>Launch the SuperVPN app after installation.</p>
            </article>

            <article class="setup-item">
              <h3>2. Allow VPN Permission</h3>
              <p>
                Android will ask for permission to create a VPN connection. Tap <strong>Allow</strong> to continue.
              </p>
            </article>

            <article class="setup-item">
              <h3>3. Connect to a VPN Server</h3>
              <p>
                Tap the connect button to start your VPN connection. SuperVPN will automatically establish a secure connection.
              </p>
            </article>

            <article class="setup-item">
              <h3>4. Start Browsing</h3>
              <p>
                Once connected, your internet traffic will be routed through the VPN connection, allowing you to browse with improved privacy.
              </p>
            </article>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import HeadNav from '@/components/HeadNav.vue';
import logoImage from '@/assets/supervpn/super-vpn-logo.webp';
import { SUPERVPN_APK_URL } from '@/config/download';

export default {
  name: 'Download',
  components: {
    HeadNav,
  },
  data() {
    return {
      logo: logoImage,
      apk: SUPERVPN_APK_URL,
      // 飞书表格 Requirement / Details 1:1
      requirementRows: [
        { label: 'Operating System', value: 'Android 7.0 or higher' },
        { label: 'Storage Space', value: 'At least 50 MB free space recommended' },
        { label: 'Root Access', value: 'Not required' },
        { label: 'Internet Connection', value: 'Required' },
      ],
    };
  },
  methods: {
    handleDownload() {
      if (this.$logEvent) {
        this.$logEvent('download_from_get_app', { page: 'get-app' });
      }
      if (this.apk && this.$global) {
        this.$global.download(this.apk);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@500;600;700&display=swap');

.get-app-page {
  --page: #f3f7fb;
  --surface: #ffffff;
  --ink: #12263a;
  --body: #445568;
  --muted: #667789;
  --line: #d5e4ef;
  --brand: #1aade8;
  --brand-mid: #2bb8ef;
  --brand-dark: #0d84b8;
  --brand-soft: rgba(26, 173, 232, 0.12);
  --band: #e7f5fc;
  --shadow: 0 18px 40px rgba(18, 38, 58, 0.08);
  --radius: 16px;
  --content: 1080px;
  background:
    radial-gradient(1200px 420px at 12% -8%, rgba(26, 173, 232, 0.14), transparent 60%),
    radial-gradient(900px 360px at 90% 8%, rgba(232, 169, 58, 0.06), transparent 55%),
    linear-gradient(180deg, #f7fbfd 0%, var(--page) 32%, #eef3f8 100%);
  color: var(--body);
  font-family: 'Manrope', 'Segoe UI', sans-serif;
  overflow-x: clip;

  .section-inner {
    width: min(var(--content), calc(100% - 48px));
    margin: 0 auto;
  }

  .hero-shell {
    position: relative;
    overflow: hidden;
    border-bottom: 1px solid rgba(26, 173, 232, 0.1);
    background:
      linear-gradient(135deg, rgba(26, 173, 232, 0.1) 0%, rgba(255, 255, 255, 0.55) 42%, rgba(232, 169, 58, 0.06) 100%),
      linear-gradient(180deg, #eaf6fc 0%, #f7fbfd 100%);
  }

  .hero-shell__glow {
    position: absolute;
    inset: auto -10% -40% 30%;
    height: 70%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(26, 173, 232, 0.18), transparent 68%);
    pointer-events: none;
  }

  .hero-section {
    position: relative;
    z-index: 1;
    padding: 56px 0 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .hero-actions {
    display: flex;
    justify-content: center;
    margin-top: 28px;
  }

  .primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-width: 240px;
    min-height: 58px;
    padding: 0 36px;
    border: 0;
    border-radius: 16px;
    color: #ffffff;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, transparent 42%),
      linear-gradient(180deg, #3bc0f0 0%, var(--brand) 48%, var(--brand-dark) 100%);
    box-shadow:
      0 14px 28px rgba(26, 173, 232, 0.34),
      inset 0 1px 0 rgba(255, 255, 255, 0.28);
    font-family: inherit;
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  }

  .primary-btn__icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  .primary-btn:hover {
    transform: translateY(-2px);
    filter: brightness(1.03);
    box-shadow:
      0 18px 34px rgba(13, 132, 184, 0.38),
      inset 0 1px 0 rgba(255, 255, 255, 0.32);
  }

  .primary-btn:active {
    transform: translateY(0);
  }

  .section {
    padding: 64px 0;
  }

  h1,
  h2,
  h3 {
    margin: 0;
    color: var(--ink);
    letter-spacing: -0.02em;
    font-family: 'Sora', 'Manrope', sans-serif;
  }

  h1 {
    max-width: none;
    font-size: clamp(30px, 4vw, 44px);
    line-height: 1.14;
    font-weight: 700;
  }

  h2 {
    position: relative;
    margin-bottom: 18px;
    padding-bottom: 12px;
    font-size: clamp(24px, 3vw, 32px);
    line-height: 1.22;
    font-weight: 700;
  }

  h2::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 56px;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--brand), rgba(232, 169, 58, 0.85));
  }

  .section-title-link {
    color: inherit;
    text-decoration: none;
  }

  .section-title-link:hover {
    color: var(--brand-dark);
    text-decoration: underline;
  }

  h3 {
    margin-bottom: 8px;
    font-size: 18px;
    line-height: 1.3;
    font-weight: 600;
  }

  p {
    margin: 0 0 14px;
    max-width: none;
    width: 100%;
    font-size: 16px;
    line-height: 1.78;
  }

  .logo-figure {
    margin: 28px 0 0;
    display: flex;
    justify-content: center;
  }

  .logo-figure img {
    width: 100%;
    max-width: 200px;
    height: auto;
    display: block;
    padding: 14px;
    border-radius: 24px;
    border: 1px solid rgba(26, 173, 232, 0.16);
    background: linear-gradient(160deg, rgba(255, 255, 255, 0.95), rgba(231, 245, 252, 0.95));
    box-shadow: var(--shadow);
  }

  .table-wrap {
    margin-top: 20px;
    overflow-x: auto;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--surface);
    box-shadow: 0 10px 28px rgba(18, 38, 58, 0.05);
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 14px 16px;
    border-bottom: 1px solid var(--line);
    text-align: left;
    vertical-align: middle;
    font-size: 15px;
    line-height: 1.4;
  }

  th {
    color: #fff;
    background: linear-gradient(180deg, var(--brand-mid) 0%, var(--brand) 100%);
    font-weight: 700;
    border-bottom: 0;
  }

  th:first-child {
    border-radius: 15px 0 0 0;
  }

  th:last-child {
    border-radius: 0 15px 0 0;
  }

  td:first-child {
    color: var(--ink);
    font-weight: 700;
    width: 38%;
  }

  tbody tr:nth-child(even) {
    background: rgba(26, 173, 232, 0.04);
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover td {
    background: rgba(26, 173, 232, 0.07);
  }

  .setup-section {
    width: 100%;
    max-width: none;
    padding-bottom: 72px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.35), transparent 30%),
      linear-gradient(135deg, var(--band) 0%, #d4eef9 100%);
    border-top: 1px solid rgba(26, 173, 232, 0.08);
  }

  .setup-section strong {
    color: var(--ink);
    font-weight: 700;
  }

  .setup-list {
    display: grid;
    gap: 12px;
    margin-top: 22px;
  }

  .setup-item {
    padding: 18px 20px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.82);
    border: 1px solid rgba(26, 173, 232, 0.12);
    border-left: 4px solid var(--brand);
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .setup-item:hover {
    transform: translateY(-2px);
    border-color: rgba(26, 173, 232, 0.28);
    box-shadow: var(--shadow);
  }

  .setup-item p {
    margin: 0;
    color: var(--body);
  }

  @media (max-width: 820px) {
    .hero-section {
      padding: 44px 0 36px;
    }

    .section {
      padding: 52px 0;
    }
  }

  @media (max-width: 560px) {
    .section-inner {
      width: min(100% - 28px, var(--content));
    }

    .hero-section {
      padding-top: 32px;
    }

    .section {
      padding: 40px 0;
    }

    h3 {
      font-size: 17px;
    }

    .logo-figure img {
      max-width: 160px;
      padding: 12px;
      border-radius: 20px;
    }

    th,
    td {
      padding: 12px;
      font-size: 14px;
    }

    .setup-section {
      padding-bottom: 52px;
    }

    .setup-item {
      padding: 16px;
    }
  }
}
</style>
